import { AppState, Query } from "../../interfaces";
import {
  eventCode,
  selectedCharterTypes,
  selectedInsuranceFunds,
  selectedStatesAlpha,
  selectedTransactionTypes,
} from "../../refdata/FilterOptions";
import { AllActions } from "../actions";
import * as constants from "../constants";

export const initialQueryState: Query = {
  filters: {},
  search: {},
  pagination: {
    resultLimit: 25,
    pageNumber: 1,
  },
  ui: {},
  sort: {
    sortField: "",
    sortOrder: "",
  },
  urls: {
    apiJson: "",
    csvDownload: "",
    browserPath: "",
    appPath: "",
    apiPath: "",
  },
  agg: {
    agg_by: "",
    agg_term_fields: "" as any,
  },
};
export const defaultStructureChangeAppState: Query = {
  ...initialQueryState,
  filters: {
    startDate: new Date(
      new Date().setFullYear(new Date().getFullYear() - 1),
    ).toLocaleDateString(),
    endDate: new Date().toLocaleDateString(),
    searchDateRadio: "EFFDATE",
    eventCode: [].concat(...eventCode.map((event) => event.value as never[])),
  },
  search: {
    name: "",
    certSearch: "",
    stateSearch: "",
    city: "",
  },
  urls: {
    appPath: "/bankfind-suite/oscr",
    apiPath: "/history",
  },
  agg: {
    agg_by: "REPORT_TYPE",
    agg_term_fields: ["CLASS"],
  },
};

export const defaultBankFindAppState: Query = {
  ...initialQueryState,
  filters: {
    cert: "",
    zip: "",
    stalp: "",
    county: "",
  },
  ui: { branchOffices: true },
  search: {
    city: "",
    name: "",
    url: "",
  },
};

export const defaultFinancialReportingAppState: Query = {
  ...initialQueryState,
  filters: {
    cert: "",
    reportPeriod: [""],
    establishedStartRange: "01/01/1792",
    establishedEndRange: new Date().toLocaleDateString(),
    inactiveStartRange: "01/01/1970",
    inactiveEndRange: new Date().toLocaleDateString(),
    financials: "",
    minimumRange: "",
    maximumRange: "",
    zip: "",
  },
  search: {
    name: "",
    city: "",
  },
  sort: {
    sortField: "CERT",
    sortOrder: "ASC",
  },
  ui: {
    incomeBasis: ["YTD"],
    unitType: ["$"],
    limitEstablishedDate: false,
    limitInactiveDate: false,
    institutionType: "banks",
    noFRdata: false,
  },
};

export const defaultPeerGroupComparisonsAppState: Query = {
  ...initialQueryState,
  filters: {
    reportPeriod: [""],
    financials: "",
  },
  ui: {
    comparisonType: "",
    peerGroups: [],
    incomeBasis: ["YTD"],
    unitType: ["$"],
    pgcStep: "step1",
    institutionExpand: true,
    customPeerGroups: [],
    standardPeerGroups: [],
    standardGroupSearchPills: [],
  },
  sort: {
    sortField: "CERT",
    sortOrder: "ASC",
  },
  pagination: {},
};

export const defaultSOD: Query = {
  ...initialQueryState,
  filters: {
    stname: "",
    stateNames: [""],
    cert: "",
    bhcRSSD: "",
    OTSDocket: "",
    rssdid: "",
    zipbr: "",
    stalp: "",
    stalpbr: "",
    msa: "",
    county: "",
    countyReset: "",
  },
  ui: {
    institutionType: "banks",
    marketSelection: [{}],
    locations: "",
    reportType: "",
    institutionTypeTimeSeries: "",
    totalsType: "",
    instType: "",
    displayResults: "",
    institutionName: "",
    partialMerger: [],
    closeModal: false,
    certAcquiring: "",
    partialMergerCheckboxes: {},
  },
  search: {
    name: "",
    bhcName: "",
    city: "",
  },
  sort: {
    sortField: "STNAME",
    sortOrder: "ASC",
  },
};

export const defaultHistoricalAppState: Query = {
  ...initialQueryState,
  filters: {
    selectedReport: "CBS",
    selectedStates: [0],
    startDate: 1934,
    endDate: new Date().getFullYear() - 1,
    apiRange: "YEAR",
  },
  ui: {
    searchPanelExpand: true,
  },
  sort: {
    sortField: "YEAR",
    sortOrder: "DESC",
  },
  fixedDisplayFields: ["YEAR", "STNAME"],
  displayFields: ["TOTAL", "BRANCHES", "OFFICES", "New_Char"],
};

export const defaultFailuresAppState: Query = {
  ...initialQueryState,
  filters: {
    startDate: 2012,
    endDate: new Date().getFullYear(),
    apiRange: "FAILYR",
    selectedStatesAlpha: selectedStatesAlpha.map((state) => state.value),
    selectedInsuranceFunds: selectedInsuranceFunds.map((funds) => funds.value),
    selectedCharterTypes: selectedCharterTypes.map((charter) => charter.value),
    selectedTransactionTypes: selectedTransactionTypes.map(
      (transaction) => transaction.value,
    ),
  },
  ui: {
    aggReport: "details",
  },
  sort: {
    sortField: "FAILDATE",
    sortOrder: "DESC",
  },
  fixedDisplayFields: [],
  displayFields: [
    "NAME",
    "CERT",
    "FIN",
    "CITYST",
    "FAILDATE",
    "SAVR",
    "RESTYPE",
    "COST",
    "RESTYPE1",
    "CHCLASS1",
    "QBFDEP",
    "QBFASSET",
  ],
};

function getApp() {
  if (window.location.pathname.includes("oscr")) {
    return "structureChange";
  }
  if (window.location.pathname.includes("financialreporting")) {
    return "financialReporting";
  }
  if (window.location.pathname.includes("historical")) {
    return "historical";
  }
  if (window.location.pathname.includes("failures")) {
    return "failures";
  }
  if (window.location.pathname.includes("peergroup")) {
    return "peerGroup";
  }
  if (window.location.pathname.includes("SOD")) {
    return "SOD";
  }
  if (window.location.pathname.includes("bulkData")) {
    return "bulkData";
  }
  return "bankFind";
}

export const initialAppState: AppState = {
  app: getApp(),
  uuid: "",
  bankFind: defaultBankFindAppState,
  structureChange: defaultStructureChangeAppState,
  financialReporting: defaultFinancialReportingAppState,
  peerGroup: defaultPeerGroupComparisonsAppState,
  SOD: defaultSOD,
  bulkData: initialQueryState,
  historical: defaultHistoricalAppState,
  failures: defaultFailuresAppState,
  prevState: {
    ...initialQueryState,
  },
  banner: "",
};

export function appState(
  state: any = initialAppState,
  action: AllActions,
): AppState {
  switch (action.type) {
    case constants.UPDATE_APP:
      return {
        ...state,
        app: action.app,
      };
    case constants.UPDATE_UUID:
      return {
        ...state,
        uuid: action.uuid,
      };
    case constants.UPDATE_FILTER:
      const updateFilters: any = { ...state[state.app].filters };
      updateFilters[action.filterField] = action.filterValue;
      return {
        ...state,
        [state.app]: {
          ...state[state.app],
          filters: updateFilters,
        },
      };
    case constants.UPDATE_PAGINATION:
      const updatePagination: any = { ...state[state.app].pagination };
      updatePagination[action.paginationField] = action.paginationValue;
      return {
        ...state,
        [state.app]: {
          ...state[state.app],
          pagination: updatePagination,
        },
      };
    case constants.UPDATE_SEARCH:
      const updateSearch: any = { ...state[state.app].search };

      updateSearch[action.searchField] = action.searchValue;
      return {
        ...state,
        [state.app]: {
          ...state[state.app],
          search: updateSearch,
        },
      };
    case constants.UPDATE_UI:
      const updateUi: any = { ...state[state.app].ui };
      updateUi[action.uiField] = action.uiValue;

      return {
        ...state,
        [state.app]: {
          ...state[state.app],
          ui: updateUi,
        },
      };
    case constants.UPDATE_DISPLAY_FIELDS:
      const updateDisplay = action.displayfields;
      return {
        ...state,
        [state.app]: {
          ...state[state.app],
          displayFields: updateDisplay,
        },
      };
    case constants.ADD_VALUE:
      const currentValues = action.filterField
        ? state[state.app].filters[action.filterField]
        : state[state.app].displayFields;
      const addDisplayFields = [...currentValues, ...action.newValues].filter(
        (val, id, array) => array.indexOf(val) === id,
      );

      const addedValues = action.filterField
        ? {
            ...state,
            [state.app]: {
              ...state[state.app],
              filters: {
                ...state[state.app].filters,
                [action.filterField]: addDisplayFields,
              },
            },
          }
        : {
            ...state,
            [state.app]: {
              ...state[state.app],
              displayFields: addDisplayFields,
            },
          };

      return addedValues;
    case constants.REMOVE_VALUE:
      const currentToRemoveValues = action.filterField
        ? state[state.app].filters[action.filterField]
        : state[state.app].displayFields;
      const filteredDisplayFields = currentToRemoveValues
        .filter((val: never) => !action.removedFields.includes(val))
        .filter((val: any) => val !== "");

      const removedValues = action.filterField
        ? {
            ...state,
            [state.app]: {
              ...state[state.app],
              filters: {
                ...state[state.app].filters,
                [action.filterField]: filteredDisplayFields,
              },
            },
          }
        : {
            ...state,
            [state.app]: {
              ...state[state.app],
              displayFields: filteredDisplayFields,
            },
          };

      return removedValues;
    case constants.RESET_STATE:
      const resetState = action.newState;
      return { ...state, [state.app]: resetState };
    case constants.SAVE_BROWSER_LOCATION_INTO_STATE:
      return {
        ...state,
        [state.app]: {
          ...state[state.app],
          urls: {
            ...state[state.app].urls,
            appPath: action.appPath,
            apiPath: action.apiPath,
          },
        },
      };

    case constants.UPDATE_BROWSER_LOCATION:
      return {
        ...state,
        [state.app]: {
          ...state[state.app],
          urls: {
            ...state[state.app]?.urls,
            browserPath: action.locationSearchString,
          },
        },
      };
    case constants.UPDATE_SORT:
      return {
        ...state,
        [state.app]: {
          ...state[state.app],
          sort: {
            ...state[state.app].sort,
            sortField: action.newSortField,
            sortOrder: action.newSortOrder,
          },
        },
      };
    case constants.UPDATE_AGG:
      return {
        ...state,
        [state.app]: {
          ...state[state.app],
          agg: {
            agg_by: action.aggBy,
            agg_term_fields: action.aggTermFields,
            agg_sum_fields: action.aggSumFields,
            subtotal_by: action.subTotalBy,
            total_fields: action.totalFields,
          },
        },
      };
    case constants.UPDATE_BANNER:
      return {
        ...state,
        banner: action.banner,
      };
    default:
      return state;
  }
}
